import { type RefObject, useLayoutEffect, useRef, useState } from 'react';

export type FitItemsLayout = 'table' | 'list';

export interface UseFitItemsPerPageOptions {
    /** thead yoksa veya ölçülemezse (px) */
    tableHeadFallbackPx?: number;
    /** tbody satırı yoksa veya ölçülemezse (px) */
    tableRowFallbackPx?: number;
    /** Kart / blok listesi: öğe yokken (px) */
    listItemFallbackPx?: number;
    /** Kenar / yuvarlama payı */
    epsilonPx?: number;
    minItems?: number;
}

const DEFAULTS: Required<UseFitItemsPerPageOptions> = {
    tableHeadFallbackPx: 56,
    tableRowFallbackPx: 52,
    listItemFallbackPx: 148,
    epsilonPx: 6,
    minItems: 1,
};

function measureTableBodyRows(container: HTMLElement, o: Required<UseFitItemsPerPageOptions>): number {
    const h = container.clientHeight;
    if (h < 1) return o.minItems;

    const thead = container.querySelector('thead');
    const theadH = thead ? thead.getBoundingClientRect().height : o.tableHeadFallbackPx;

    const rows = container.querySelectorAll('tbody tr');
    let rowH = o.tableRowFallbackPx;
    if (rows.length >= 2) {
        const a = rows[0].getBoundingClientRect();
        const b = rows[1].getBoundingClientRect();
        rowH = Math.max(1, b.top - a.top);
    } else if (rows.length === 1) {
        rowH = Math.max(1, rows[0].getBoundingClientRect().height);
    }

    const usable = Math.max(0, h - theadH - o.epsilonPx);
    return Math.max(o.minItems, Math.floor(usable / rowH));
}

function measureListItems(container: HTMLElement, itemSelector: string, o: Required<UseFitItemsPerPageOptions>): number {
    const h = container.clientHeight;
    if (h < 1) return o.minItems;

    const items = container.querySelectorAll(itemSelector);
    let itemH = o.listItemFallbackPx;
    if (items.length >= 2) {
        const a = items[0].getBoundingClientRect();
        const b = items[1].getBoundingClientRect();
        itemH = Math.max(1, b.top - a.top);
    } else if (items.length === 1) {
        const a = items[0].getBoundingClientRect();
        itemH = Math.max(1, a.height + 12);
    }

    const usable = Math.max(0, h - o.epsilonPx);
    return Math.max(o.minItems, Math.floor(usable / itemH));
}

/**
 * Görünür liste alanına (container) kaç öğe sığacağını ResizeObserver ile hesaplar.
 * Tablo: thead + satır yüksekliği mümkünse DOM’dan; kart listesi: [data-fit-list-item] öğelerinden.
 * Evrensel yol: sabit “sayfa başına N” yerine container ölçüsü + tekil öğe yüksekliği.
 */
export function useFitItemsPerPage(
    containerRef: RefObject<HTMLElement | null>,
    layout: FitItemsLayout,
    enabled: boolean,
    options?: UseFitItemsPerPageOptions & { listItemSelector?: string },
    /** Aynı ref farklı DOM’a bağlandığında (boş ↔ tablo ↔ kart) yeniden ölçüm */
    measureContextKey?: unknown
): number {
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const [count, setCount] = useState(() => DEFAULTS.minItems);

    useLayoutEffect(() => {
        if (!enabled) return;

        const measure = () => {
            const el = containerRef.current;
            if (!el) return;

            const o = { ...DEFAULTS, ...optionsRef.current } as Required<UseFitItemsPerPageOptions>;
            const listItemSelector = optionsRef.current?.listItemSelector ?? '[data-fit-list-item]';

            const next =
                layout === 'table'
                    ? measureTableBodyRows(el, o)
                    : measureListItems(el, listItemSelector, o);

            setCount((c) => (c === next ? c : next));
        };

        measure();
        const raf = requestAnimationFrame(measure);

        const el = containerRef.current;
        let ro: ResizeObserver | undefined;
        if (el && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => measure());
            ro.observe(el);
        }

        window.addEventListener('resize', measure);
        return () => {
            cancelAnimationFrame(raf);
            ro?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [enabled, layout, containerRef, measureContextKey]);

    return count;
}
